import React from "react";
import { useQuery, useMutation } from "@apollo/client";
import {
  BarChart,
  Bar,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import Chip from "@mui/material/Chip";
import Grid from "@mui/material/Grid";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import MuiTooltip from "@mui/material/Tooltip";
import InfoIcon from "@mui/icons-material/Info";
import {
  DataGrid,
  GRID_CHECKBOX_SELECTION_COL_DEF,
  GridFooter,
  GridFooterContainer,
  GridToolbarContainer,
} from "@mui/x-data-grid";

import {
  GET_EMPLOYEE_BY_EID,
  GET_EMPLOYEE_TARGET,
  GET_TOTAL_CUSTOMER,
} from "../../graphql/Employee";
import {
  CLAIM_ORDER,
  CREATE_CLAIM_ORDER,
  GET_CLAIMS_BY_EMPLOYEE,
} from "../../graphql/Order";
import GlobalContext from "../../utils/GlobalContext";
import dayjs from "dayjs";
import { generateArrayOfYears, checkLevel, nextLevel } from "../../utils";

const EmployeeDetail = (props) => {
  const { eid } = props?.match?.params;
  const _eid = props.eid;

  const { setGlobalLoading, setSnack } = React.useContext(GlobalContext);
  const [employee, setEmployee] = React.useState({});
  const [dashboard, setDashboard] = React.useState({});
  const [percentage, setPercentage] = React.useState(0);
  const [year, setYear] = React.useState(new Date().getFullYear());
  const [companies, setCompanies] = React.useState([]);
  const [currentLevel, setCurrentLevel] = React.useState("");
  const [target, setTarget] = React.useState({ level: "-", estimate: 0 });
  const [achievement, setAchievement] = React.useState(0);
  const [claims, setClaims] = React.useState([]);
  const [availableBonus, setAvailableBonus] = React.useState(0);
  const [claimBonus, setClaimBonus] = React.useState(0);
  const [selectedClaims, setSelectedClaims] = React.useState([]);
  const [totalClaim, setTotalClaim] = React.useState({});
  const years = generateArrayOfYears();

  const columns = [
    {
      field: "id",
      headerName: "Nomor",
      width: 90,
      renderCell: (params) => `${params.api.getRowIndex(params.row.id) + 1}.`,
    },
    {
      field: "name",
      headerName: "Customer",
      flex: 1,
    },
    {
      field: "total",
      headerName: "Jumlah",
      flex: 1,
    },
  ];

  const columnsClaim = [
    {
      field: "invoice_number",
      headerName: "Nomor Tagihan",
      flex: 1,
    },
    {
      field: "total_profit",
      headerName: "Laba",
      flex: 1,
      valueGetter: (params) =>
        `Rp. ${params?.row?.total_profit.toLocaleString("id-ID")}`,
    },
    {
      field: "estimate_profit",
      headerName: "Estimasi 5%",
      flex: 1,
      valueGetter: (params) =>
        `Rp. ${params?.row?.estimate_profit.toLocaleString("id-ID")}`,
    },
    {
      ...GRID_CHECKBOX_SELECTION_COL_DEF,
      width: 100,
    },
  ];

  const columnsClaimed = [
    {
      field: "id",
      headerName: "Nomor",
      width: 90,
      renderCell: (params) => `${params.api.getRowIndex(params.row.id) + 1}.`,
    },
    {
      field: "created_at",
      headerName: "Tanggal Klaim",
      flex: 1,
    },
    {
      field: "total_claim",
      headerName: "Total Klaim",
      flex: 1,
      valueGetter: (params) =>
        `Rp. ${params?.row?.total_claim?.toLocaleString("id-ID")}`,
    },
  ];

  const { loading, data } = useQuery(GET_EMPLOYEE_BY_EID, {
    variables: { eid },
    fetchPolicy: "network-only",
    onError: (e) => console.log("error getting selor:", e),
  });
  const { loading: loadingDashboard, data: dataDashboard } = useQuery(
    GET_EMPLOYEE_TARGET,
    {
      variables: { eid, year },
      fetchPolicy: "network-only",
      onError: (e) => console.log("error getting selor:", e),
    },
  );
  const { loading: loadingCompanies, data: dataCompanies } = useQuery(
    GET_TOTAL_CUSTOMER,
    {
      variables: { eid, year },
      fetchPolicy: "network-only",
      onError: (e) => console.log("error getting selor:", e),
    },
  );

  const {
    loading: loadingClaim,
    data: dataClaim,
    refetch,
  } = useQuery(CLAIM_ORDER, {
    variables: { employee_id: eid, year },
    fetchPolicy: "network-only",
    onError: (e) => console.log("error getting selor:", e),
  });

  const {
    loading: loadingTotalClaim,
    data: dataTotalClaim,
    refetch: refetch_total_claim,
  } = useQuery(GET_CLAIMS_BY_EMPLOYEE, {
    variables: { employee_id: eid, year },
    fetchPolicy: "network-only",
    onError: (e) => console.log("error getting selor:", e),
  });

  React.useEffect(() => {
    if (loading) {
      setGlobalLoading(true);
    }
  }, []);

  React.useEffect(() => {
    if (data) {
      setEmployee(data.getEmployeeByEid);
      setGlobalLoading(false);
    }
  }, [data, setGlobalLoading]);

  React.useEffect(() => {
    if (dataClaim) {
      const claims = dataClaim?.findClaimOrderByYear;
      setClaims(claims);
      const ab = claims
        ?.filter((c) => !c.claimed)
        ?.reduce(
          (n, { estimate_profit }) => Number(n) + Number(estimate_profit),
          0,
        );
      setAvailableBonus(ab);
      setGlobalLoading(false);
    }
  }, [dataClaim, setGlobalLoading]);

  React.useEffect(() => {
    if (dataCompanies) {
      setCompanies(dataCompanies.getTotalCustomer);
      setGlobalLoading(false);
    }
  }, [dataCompanies, setGlobalLoading]);

  React.useEffect(() => {
    if (dataTotalClaim) {
      setTotalClaim(dataTotalClaim.getClaimsByEmployee);
      setGlobalLoading(false);
    }
  }, [dataTotalClaim, setGlobalLoading]);

  React.useEffect(() => {
    if (dataDashboard) {
      setDashboard(dataDashboard.getEmployeeTarget);
      const _percentage = Math.round(
        (dataDashboard?.getEmployeeTarget?.total_profit /
          dataDashboard?.getEmployeeTarget?.company_profit) *
          100,
      );

      if (_percentage) setPercentage(_percentage);
      setGlobalLoading(false);

      // if (year === new Date().getFullYear()) {
      const profit = dataDashboard?.getEmployeeTarget?.total_profit;
      const level = checkLevel(profit);
      setCurrentLevel(level);
      const lvl = nextLevel(level, profit);
      setTarget({ level: lvl.level, estimate: lvl.estimate });
      setAchievement(profit);
      // }
    }
  }, [dataDashboard, year]);

  const CustomFooter = () => {
    return (
      <GridFooterContainer>
        <Box style={{ marginLeft: 10 }}>
          <Box>
            <Typography variant="subtitle2">
              Bonus Tersedia Rp. {availableBonus?.toLocaleString("id-ID")}
            </Typography>
          </Box>
          <Box>
            <Typography variant="subtitle2">
              Bonus Terklaim Rp. {claimBonus?.toLocaleString("id-ID")}
            </Typography>
          </Box>
        </Box>
        <GridFooter
          sx={{
            border: "none",
          }}
        />
      </GridFooterContainer>
    );
  };

  const CustomToolbar = () => {
    return (
      <GridToolbarContainer
        style={{
          justifyContent: "flex-end",
          paddingRight: "15px",
          paddingTop: "10px",
        }}
      >
        <Box>
          <Button
            variant="contained"
            size="small"
            disabled={!selectedClaims?.length}
            onClick={handleSubmit}
          >
            Klaim
          </Button>
        </Box>
      </GridToolbarContainer>
    );
  };

  const [createClaimOrder] = useMutation(CREATE_CLAIM_ORDER, {
    onCompleted: (d) => {
      setGlobalLoading(true);
      setSnack({
        variant: "success",
        message: "Data klaim berhasil disimpan",
        opened: true,
      });
      setGlobalLoading(false);
      refetch();
      refetch_total_claim();
      return d?.createClaimOrder;
    },
    onError: (e) => {
      console.log("error update invoice: ", e);
      let message = "Data klaim gagal disimpan ";

      setGlobalLoading(false);
      setSnack({
        variant: "error",
        message,
        opened: true,
      });
    },
  });

  const handleSubmit = async () => {
    const total_claim =
      selectedClaims?.reduce(
        (n, { estimate_profit }) => Number(n) + Number(estimate_profit),
        0,
      ) ?? 0;
    const order_ids = selectedClaims?.map((x) => x.id) ?? [];
    const data = {
      employee_id: employee?.id,
      total_claim,
      order_ids,
    };

    return createClaimOrder({ variables: { data } });
  };

  return (
    <React.Fragment>
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2, sm: 3 },
          mb: 3,
          border: 1,
          borderColor: "divider",
          borderRadius: 3,
        }}
      >
        <Grid container spacing={3} alignItems="center">
          <Grid
            item
            xs={12}
            sm={4}
            md={3}
            sx={{
              display: "flex",
              justifyContent: { xs: "center", sm: "flex-start" },
            }}
          >
            <Box
              sx={{
                width: { xs: 150, sm: 170, md: 190 },
                height: { xs: 200, sm: 225, md: 250 },
                overflow: "hidden",
                borderRadius: 2,
                bgcolor: "action.hover",
                boxShadow: 2,
              }}
            >
              <Box
                component="img"
                src={
                  employee?.image ||
                  "https://res.cloudinary.com/dkv1a9nmy/image/upload/v1741870576/employees/blank-profile-picture-973460_960_720_gtz20l.webp"
                }
                alt={employee?.name ? `Foto ${employee.name}` : "Foto pegawai"}
                sx={{
                  display: "block",
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                  objectPosition: "center top",
                }}
              />
            </Box>
          </Grid>
          <Grid item xs={12} sm={8} md={9} sx={{ minWidth: 0 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12}>
                <Typography variant="overline" color="text.secondary">
                  Profil Marketing
                </Typography>
                <Typography variant="h5" fontWeight={700} sx={{ mb: 1 }}>
                  {employee?.name || "-"}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  fontWeight={700}
                >
                  Tempat / Tanggal Lahir
                </Typography>
                <Typography variant="body1" sx={{ overflowWrap: "anywhere" }}>
                  {employee?.place_of_birth || "-"},{" "}
                  {dayjs(employee?.date_of_birth).format("DD/MM/YYYY")}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  fontWeight={700}
                >
                  No. Telepon
                </Typography>
                <Typography variant="body1" sx={{ overflowWrap: "anywhere" }}>
                  {employee?.phone || "-"}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  fontWeight={700}
                >
                  ID Perusahaan
                </Typography>
                <Typography variant="body1" sx={{ overflowWrap: "anywhere" }}>
                  {employee?.company_id ?? "-"}
                </Typography>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  fontWeight={700}
                >
                  Status
                </Typography>
                <Box sx={{ mt: 0.5 }}>
                  <Chip
                    label={employee?.status || "-"}
                    variant="filled"
                    color="info"
                    size="small"
                  />
                </Box>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  fontWeight={700}
                >
                  Level
                </Typography>
                <Box sx={{ mt: 0.5 }}>
                  <Chip
                    label={checkLevel(
                      dashboard.total_profit,
                    ).toLocaleUpperCase()}
                    variant="filled"
                    color="success"
                    size="small"
                  />
                </Box>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth size="small">
                  <InputLabel id="year">Tahun</InputLabel>
                  <Select
                    labelId="year"
                    id="year"
                    name="year"
                    label="Tahun"
                    value={year}
                    onChange={(e) => setYear(e.target.value)}
                  >
                    {years.map((x, i) => (
                      <MenuItem key={i} value={x}>
                        {x}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Paper>

      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
          <Typography variant="h6" fontWeight={700}>
            Pencapaian & Target
          </Typography>
          <MuiTooltip
            placement="top"
            title={
              <div style={{ whiteSpace: "pre-line" }}>
                {`BEGINNER : 1.000.000 - 25.000.000
                \nINTERMEDIATE : 25.000.001 - 50.000.000
                \nADVANCED : 50.000.001 - 100.000.000
                \nPRO : 100.000.001 - 200.000.000
                \nPRO ++ : 200.000.001 - 500.000.000
                \nSAKTI : 500.000.001 - 1.000.000.000
                \nUNSTOPPABLE : 1.000.000.001 - 5.000.000.000
                `}
              </div>
            }
          >
            <IconButton size="small" aria-label="Informasi level pencapaian">
              <InfoIcon />
            </IconButton>
          </MuiTooltip>
        </Box>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              sm: "repeat(2, minmax(0, 1fr))",
              lg: "repeat(5, minmax(0, 1fr))",
            },
            gap: 2,
          }}
        >
          <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, minWidth: 0 }}>
            <Typography
              variant="caption"
              color="text.secondary"
              fontWeight={700}
            >
              Pencapaian
            </Typography>
            <Typography
              variant="h6"
              fontWeight={700}
              sx={{ overflowWrap: "anywhere" }}
            >
              Rp. {achievement?.toLocaleString("id-ID") ?? 0}
            </Typography>
          </Paper>
          <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, minWidth: 0 }}>
            <Typography
              variant="caption"
              color="text.secondary"
              fontWeight={700}
            >
              Target
            </Typography>
            <Typography
              variant="h6"
              fontWeight={700}
              sx={{ overflowWrap: "anywhere" }}
            >
              {target?.level}
            </Typography>
          </Paper>
          <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, minWidth: 0 }}>
            <Typography
              variant="caption"
              color="text.secondary"
              fontWeight={700}
            >
              Estimasi Target Nominal
            </Typography>
            <Typography
              variant="h6"
              fontWeight={700}
              sx={{ overflowWrap: "anywhere" }}
            >
              Rp. {target?.estimate?.toLocaleString("id-ID")}
            </Typography>
          </Paper>
          <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, minWidth: 0 }}>
            <Typography
              variant="caption"
              color="text.secondary"
              fontWeight={700}
            >
              Target Hingga
            </Typography>
            <Typography
              variant="h6"
              fontWeight={700}
              sx={{ overflowWrap: "anywhere" }}
            >
              Desember {year}
            </Typography>
          </Paper>
          <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, minWidth: 0 }}>
            <Typography
              variant="caption"
              color="text.secondary"
              fontWeight={700}
            >
              Bonus Klaim Tahun {year}
            </Typography>
            <Typography
              variant="h6"
              fontWeight={700}
              sx={{ overflowWrap: "anywhere" }}
            >
              Rp. {totalClaim?.total?.toLocaleString("id-ID") ?? 0}
            </Typography>
          </Paper>
        </Box>
      </Box>

      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Paper
            variant="outlined"
            sx={{ p: { xs: 1.5, sm: 2.5 }, borderRadius: 2, minWidth: 0 }}
          >
            <Typography variant="h6" fontWeight={700} sx={{ mb: 1 }}>
              Ringkasan Performa
            </Typography>
            <Box
              sx={{ width: "100%", height: { xs: 440, sm: 380 }, minWidth: 0 }}
            >
              <ResponsiveContainer id="2" width="100%" height="100%">
                <BarChart
                  data={[dashboard]}
                  margin={{
                    top: 5,
                    right: 30,
                    left: 20,
                    bottom: 5,
                  }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  {/* <XAxis
                    tickFormatter={(value) => `Cawu ${value}`}
                    dataKey='quarterly'
                  /> */}
                  <YAxis
                    tickFormatter={(value) =>
                      new Intl.NumberFormat("id", {
                        notation: "compact",
                        compactDisplay: "long",
                      }).format(value)
                    }
                  />
                  <Tooltip
                    labelFormatter={(value) => `Pencapaian Target`}
                    formatter={(value, name, props) => {
                      if (name === "total_profit") {
                        return [
                          `Rp. ${new Intl.NumberFormat("id").format(value)}`,
                          "Total Laba",
                          props,
                        ];
                      }

                      if (name === "pending_profit") {
                        return [
                          `Rp. ${new Intl.NumberFormat("id").format(value)}`,
                          "Laba Tertahan",
                          props,
                        ];
                      }

                      if (name === "personal_debt") {
                        return [
                          `Rp. ${new Intl.NumberFormat("id").format(value)}`,
                          "Hutang Pribadi",
                          props,
                        ];
                      }

                      if (name === "selling_profit") {
                        return [
                          `Rp. ${new Intl.NumberFormat("id").format(value)}`,
                          "Laba Penjualan",
                          props,
                        ];
                      }

                      if (name === "invoice_profit") {
                        return [
                          `Rp. ${new Intl.NumberFormat("id").format(value)}`,
                          "Laba Sisipan Nota",
                          props,
                        ];
                      }

                      if (name === "total_platform_fee_bill") {
                        return [
                          `Rp. ${new Intl.NumberFormat("id").format(value)}`,
                          "Laba Sisipan Tagihan",
                          props,
                        ];
                      }
                    }}
                  />
                  <Bar dataKey="total_profit" fill="#8884d8" />
                  <Bar dataKey="pending_profit" fill="#82ca9d" />
                  <Bar dataKey="personal_debt" fill="#84d8c7" />
                  <Bar dataKey="selling_profit" fill="#d8d884" />
                  <Bar dataKey="invoice_profit" fill="#CBA0AE" />
                  <Bar dataKey="total_platform_fee_bill" fill="#3914f5" />
                  <Legend
                    wrapperStyle={{ fontSize: 12 }}
                    payload={[
                      {
                        id: "1",
                        type: "circle",
                        value: "Total Laba",
                        color: "#8884d8",
                      },
                      {
                        id: "2",
                        type: "circle",
                        value: "Laba Tertahan",
                        color: "#82ca9d",
                      },
                      {
                        id: "3",
                        type: "circle",
                        value: "Hutang Pribadi",
                        color: "#84d8c7",
                      },
                      {
                        id: "4",
                        type: "circle",
                        value: "Laba Penjualan",
                        color: "#d8d884",
                      },
                      {
                        id: "5",
                        type: "circle",
                        value: "Laba Sisipan Nota",
                        color: "#CBA0AE",
                      },
                      {
                        id: "6",
                        type: "circle",
                        value: "Laba Sisipan Tagihan",
                        color: "#3914f5",
                      },
                    ]}
                  />
                </BarChart>
              </ResponsiveContainer>
            </Box>
          </Paper>
        </Grid>
        <Grid item xs={12}>
          <Typography variant="caption">
            * laba yang dihasilkan {percentage}% dari laba perusahaan
          </Typography>
        </Grid>
        <Grid item xs={12} sm={12} md={6} lg={6}>
          <Paper
            variant="outlined"
            sx={{ p: { xs: 1.5, sm: 2 }, borderRadius: 2, minWidth: 0 }}
          >
            <Typography variant="h6" fontWeight={700} sx={{ mb: 1.5 }}>
              Pelanggan Teratas
            </Typography>
            <Box
              sx={{ height: { xs: 360, sm: 400 }, width: "100%", minWidth: 0 }}
            >
              <DataGrid
                rows={companies}
                columns={columns}
                pageSize={5}
                rowsPerPageOptions={[5]}
                disableSelectionOnClick
              />
            </Box>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={12} md={6} lg={6}>
          <Paper
            variant="outlined"
            sx={{ p: { xs: 1.5, sm: 2 }, borderRadius: 2, minWidth: 0 }}
          >
            <Typography variant="h6" fontWeight={700} sx={{ mb: 1.5 }}>
              Riwayat Bonus Diklaim
            </Typography>
            <Box
              sx={{ height: { xs: 360, sm: 400 }, width: "100%", minWidth: 0 }}
            >
              <DataGrid
                rows={totalClaim?.data ?? []}
                columns={columnsClaimed}
                pageSize={5}
                rowsPerPageOptions={[5]}
                disableSelectionOnClick
              />
            </Box>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={12} md={12} lg={12}>
          <Paper
            variant="outlined"
            sx={{ p: { xs: 1.5, sm: 2 }, borderRadius: 2, minWidth: 0 }}
          >
            <Typography variant="h6" fontWeight={700} sx={{ mb: 1.5 }}>
              Bonus Menunggu Klaim
            </Typography>
            <Box
              sx={{ height: { xs: 360, sm: 400 }, width: "100%", minWidth: 0 }}
            >
              <DataGrid
                rows={claims}
                columns={columnsClaim}
                pageSize={5}
                rowsPerPageOptions={[5]}
                checkboxSelection={_eid === eid}
                isRowSelectable={(params) => !params.row.claimed}
                onSelectionModelChange={(ids) => {
                  const selectedIDs = new Set(ids);
                  const selectedRowData = claims.filter((row) =>
                    selectedIDs.has(row.id.toString()),
                  );
                  const bonus = selectedRowData?.reduce(
                    (n, { estimate_profit }) =>
                      Number(n) + Number(estimate_profit),
                    0,
                  );

                  setClaimBonus(bonus);
                  setSelectedClaims(selectedRowData);
                }}
                components={{
                  Footer: CustomFooter,
                  Toolbar: _eid !== eid ? null : CustomToolbar,
                }}
              />
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </React.Fragment>
  );
};

export default EmployeeDetail;
